/* Rental Yield & Property Income Calculator Logic — Math, Charts & CSV Export */

let ryDonutChart = null;
let ryGrowthChart = null;

function getRyInputs() {
  const getVal = (id, def = 0) => {
    const el = document.getElementById(id);
    if (!el) return def;
    const v = +el.value;
    return isNaN(v) ? def : v;
  };

  return {
    value: getVal('ryValue', 10000000),
    rent: getVal('ryRent', 30000),
    expenses: getVal('ryExpenses', 25000),
    vacancy: getVal('ryVacancy', 0),
  };
}

function updateRyUrlParams(params) {
  try {
    const url = new URL(window.location);
    url.searchParams.set('value', params.value);
    url.searchParams.set('rent', params.rent);
    url.searchParams.set('expenses', params.expenses);
    url.searchParams.set('vacancy', params.vacancy);
    window.history.replaceState({}, '', url);
  } catch (e) {}
}

function parseRyUrlParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const value = searchParams.get('value');
  const rent = searchParams.get('rent');
  const expenses = searchParams.get('expenses');
  const vacancy = searchParams.get('vacancy');

  return {
    value: value !== null ? +value : null,
    rent: rent !== null ? +rent : null,
    expenses: expenses !== null ? +expenses : null,
    vacancy: vacancy !== null ? +vacancy : null,
  };
}

function calculateRy(params) {
  const { value, rent, expenses, vacancy } = params;

  const grossAnnualRent = rent * 12 * (1 - vacancy / 100);
  const grossYield = value > 0 ? (grossAnnualRent / value) * 100 : 0;

  const netAnnualIncome = Math.max(0, grossAnnualRent - expenses);
  const netYield = value > 0 ? (netAnnualIncome / value) * 100 : 0;

  const schedule = [];
  let currentRent = rent;
  let cumCashFlow = 0;

  for (let yr = 1; yr <= 10; yr++) {
    const grossRentYr = currentRent * 12 * (1 - vacancy / 100);
    const expensesYr = expenses * Math.pow(1.03, yr - 1); // 3% annual expense inflation
    const netIncomeYr = Math.max(0, grossRentYr - expensesYr);
    cumCashFlow += netIncomeYr;

    schedule.push({
      year: yr,
      monthlyRent: Math.round(currentRent),
      grossAnnualRent: Math.round(grossRentYr),
      expenses: Math.round(expensesYr),
      netIncome: Math.round(netIncomeYr),
      cumCashFlow: Math.round(cumCashFlow),
    });

    currentRent *= 1.05; // 5% annual rent escalation
  }

  return {
    value: Math.round(value),
    rent: Math.round(rent),
    grossAnnualRent: Math.round(grossAnnualRent),
    grossYield: +grossYield.toFixed(2),
    netAnnualIncome: Math.round(netAnnualIncome),
    netYield: +netYield.toFixed(2),
    schedule,
  };
}

function updateDisplays(params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('ryValueDisplay', fmt(params.value));
  setTxt('ryRentDisplay', fmt(params.rent));
  setTxt('ryExpensesDisplay', fmt(params.expenses));
  setTxt('ryVacancyDisplay', `${params.vacancy}%`);
}

function renderRyResults(result, params) {
  const setTxt = (id, txt) => {
    const el = document.getElementById(id);
    if (el) el.textContent = txt;
  };

  setTxt('ryVerdictHeadline', `Gross Yield: ${result.grossYield}% | Net Yield: ${result.netYield}%`);
  setTxt('ryVerdictDetail', `Your property valued at ${fmt(params.value)} generates ${fmt(result.grossAnnualRent)} gross annual rent, resulting in a net annual cash flow of ${fmt(result.netAnnualIncome)} after operating expenses.`);

  setTxt('ryGrossRentStatValue', fmt(result.grossAnnualRent));
  setTxt('ryNetIncomeStatValue', fmt(result.netAnnualIncome));
  setTxt('ryGrossYieldMetricValue', `${result.grossYield}%`);
  setTxt('ryNetYieldMetricValue', `${result.netYield}%`);
  setTxt('ryCashFlowMetricValue', fmt(result.netAnnualIncome));

  renderTable(result.schedule);

  try {
    if (typeof Chart !== 'undefined') {
      renderDonutChart(result.netAnnualIncome, params.expenses, params.value);
      renderGrowthChart(result.schedule);
    }
  } catch (err) {
    console.warn('Rental Yield Chart rendering error:', err);
  }
}

function renderDonutChart(netIncome, expenses, propertyValue) {
  const ctx = document.getElementById('ryDonutChart');
  if (!ctx) return;

  if (ryDonutChart) ryDonutChart.destroy();

  ryDonutChart = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Net Rental Income', 'Operating Expenses', 'Property Acquisition Base'],
      datasets: [
        {
          data: [netIncome, expenses, propertyValue],
          backgroundColor: [cssVar('--chart-buy'), cssVar('--chart-rent'), '#3B82F6'],
          borderColor: cssVar('--bg-card'),
          borderWidth: 3,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      cutout: '72%',
    },
  });
}

function renderGrowthChart(schedule) {
  const ctx = document.getElementById('ryGrowthChart');
  if (!ctx) return;

  const labels = schedule.map((r) => `Yr ${r.year}`);
  const cumCashFlowData = schedule.map((r) => r.cumCashFlow);
  const netIncomeData = schedule.map((r) => r.netIncome);

  if (ryGrowthChart) ryGrowthChart.destroy();

  ryGrowthChart = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Cumulative Cash Flow',
          data: cumCashFlowData,
          borderColor: cssVar('--chart-buy'),
          backgroundColor: 'rgba(52, 211, 153, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
        {
          label: 'Annual Net Income',
          data: netIncomeData,
          borderColor: cssVar('--chart-rent'),
          backgroundColor: 'rgba(96, 165, 250, 0.08)',
          fill: true,
          tension: 0.3,
          pointRadius: 4,
          borderWidth: 2.5,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: cssVar('--chart-tick'), usePointStyle: true, pointStyle: 'circle', padding: 16 },
        },
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg'),
          borderColor: cssVar('--tooltip-border'),
          borderWidth: 1,
          titleColor: cssVar('--tooltip-title'),
          bodyColor: cssVar('--tooltip-body'),
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: cssVar('--chart-grid') },
          ticks: { color: cssVar('--chart-tick') },
        },
        y: {
          grid: { color: cssVar('--chart-grid') },
          ticks: {
            color: cssVar('--chart-tick'),
            callback: (v) => fmtCompact(v),
          },
        },
      },
    },
  });
}

function renderTable(schedule) {
  const tbody = document.getElementById('ryScheduleBody');
  if (!tbody) return;

  tbody.innerHTML = schedule.map((row) => `
    <tr>
      <td>${row.year}</td>
      <td>${fmt(row.monthlyRent)}</td>
      <td class="advantage-buy">${fmt(row.grossAnnualRent)}</td>
      <td>-${fmt(row.expenses)}</td>
      <td class="advantage-buy">${fmt(row.netIncome)}</td>
      <td class="advantage-renter" style="font-weight: 600;">${fmt(row.cumCashFlow)}</td>
    </tr>
  `).join('');
}

function exportRyCsv() {
  const params = getRyInputs();
  const result = calculateRy(params);
  const headers = ['Year', 'Monthly Rent', 'Gross Annual Rent', 'Operating Expenses', 'Net Annual Income', 'Cumulative Cash Flow'];
  const rows = result.schedule.map((r) => [
    r.year, r.monthlyRent, r.grossAnnualRent, r.expenses, r.netIncome, r.cumCashFlow
  ]);

  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `rental-yield-10yr-projection.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function applyRyCountryRanges(country, overrideParams = {}) {
  if (!country) return;
  const curr = country.currency || 'USD';

  let defaultValue = 10000000;
  let maxPropertyPrice = 50000000; // Cap property price at 5 Cr (50,000,000)
  let stepValue = 500000;

  if (curr === 'INR') {
    defaultValue = 10000000;
    maxPropertyPrice = 50000000;
    stepValue = 500000;
  } else if (curr === 'JPY') {
    defaultValue = 100000000;
    maxPropertyPrice = 500000000;
    stepValue = 5000000;
  }

  const vSlider = document.getElementById('ryValue');
  if (vSlider) {
    vSlider.max = maxPropertyPrice;
    vSlider.step = stepValue;
    vSlider.value = overrideParams.value ?? defaultValue;
  }

  const rSlider = document.getElementById('ryRent');
  if (rSlider && overrideParams.rent !== null) {
    rSlider.value = overrideParams.rent;
  }

  const eSlider = document.getElementById('ryExpenses');
  if (eSlider && overrideParams.expenses !== null) {
    eSlider.value = overrideParams.expenses;
  }

  const vacSlider = document.getElementById('ryVacancy');
  if (vacSlider && overrideParams.vacancy !== null) {
    vacSlider.value = overrideParams.vacancy;
  }
}

function recalculateRy() {
  const params = getRyInputs();
  updateDisplays(params);
  updateRyUrlParams(params);
  const result = calculateRy(params);
  renderRyResults(result, params);
}

function initRyCalculator() {
  if (!document.getElementById('ryValue')) return;

  const urlParams = parseRyUrlParams();

  initCountry((country) => {
    applyRyCountryRanges(country, urlParams);
    recalculateRy();
  });

  const inputs = ['ryValue', 'ryRent', 'ryExpenses', 'ryVacancy'];
  inputs.forEach((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.addEventListener('input', recalculateRy);
      el.addEventListener('change', recalculateRy);
    }
  });

  const exportBtn = document.getElementById('exportRyCsv');
  if (exportBtn) {
    exportBtn.addEventListener('click', exportRyCsv);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initRyCalculator();
});
